import type { Transition, Variants } from 'framer-motion';

/**
 * The only motion values the app uses. Every animation goes through these so
 * timing stays coherent and `prefers-reduced-motion` is honoured in one place:
 * pass the user's preference to `motionSafe` / `pageVariants` and every
 * animation degrades to an instant state change.
 *
 * The same four tokens exist as CSS variables in globals.css
 * (--motion-micro, --motion-panel, --motion-page) for CSS-only transitions.
 */

type Bezier = [number, number, number, number];

export const EASE: Record<'micro' | 'panel' | 'page', Bezier> = {
  /** Hover, press, toggles, checkbox, chips. */
  micro: [0.2, 0, 0, 1],
  /** Popovers, drawers, quick-edit panel, dropdowns. */
  panel: [0.32, 0.72, 0, 1],
  /** Route change, view switch, month change. */
  page: [0.22, 1, 0.36, 1],
};

/** Seconds, as framer-motion expects. */
export const DURATION: Record<'micro' | 'panel' | 'page', number> = {
  micro: 0.12,
  panel: 0.22,
  page: 0.28,
};

export const TRANSITION: Record<'micro' | 'panel' | 'page', Transition> = {
  micro: { duration: DURATION.micro, ease: EASE.micro },
  panel: { duration: DURATION.panel, ease: EASE.panel },
  page: { duration: DURATION.page, ease: EASE.page },
};

export const SPRING_VALUES = { stiffness: 380, damping: 32 } as const;

/** Drag and drop, badge count changes, escalation ladder step. */
export const SPRING: Transition = { type: 'spring', ...SPRING_VALUES };

/** What every transition becomes under `prefers-reduced-motion`. */
export const INSTANT: Transition = { duration: 0 };

/** Pick the real transition, or an instant one when the user asked for less motion. */
export function motionSafe(transition: Transition, reducedMotion: boolean): Transition {
  return reducedMotion ? INSTANT : transition;
}

/** Route change: content fades in from 6px below. Reduced motion: no movement, no fade. */
export function pageVariants(reducedMotion: boolean): Variants {
  if (reducedMotion) {
    return { initial: { opacity: 1, y: 0 }, animate: { opacity: 1, y: 0 } };
  }
  return {
    initial: { opacity: 0, y: 6 },
    animate: { opacity: 1, y: 0, transition: TRANSITION.page },
  };
}

/** Occasion card actions reveal with this stagger (seconds). */
export const STAGGER_ACTIONS = 0.04;

/** Never animate layout on lists longer than this. */
export const LAYOUT_ANIMATION_MAX_ITEMS = 30;

/** Only the first rows of a list get an enter animation. */
export const ENTER_ANIMATION_MAX_ROWS = 12;

export function canAnimateLayout(itemCount: number, reducedMotion: boolean): boolean {
  return !reducedMotion && itemCount <= LAYOUT_ANIMATION_MAX_ITEMS;
}

export function shouldAnimateRowEnter(index: number, reducedMotion: boolean): boolean {
  return !reducedMotion && index < ENTER_ANIMATION_MAX_ROWS;
}

/** Shared layoutId values, so two components never collide on one by accident. */
export const LAYOUT_ID = {
  sidebarActive: 'sidebar-active-pill',
  inboxSelectedRow: 'inbox-selected-row',
  settingsActive: 'settings-active-pill',
  billingInterval: 'billing-interval-thumb',
} as const;
